import React, { useState, useEffect } from 'react';
import { BarChart3, PieChart, TrendingUp, Users, Calendar, Filter, Download } from 'lucide-react';
import api from '../../services/api';

const statusColors = {
  'Yeni': '#3b82f6', 'İşleme Alındı': '#8b5cf6', 'Devam Ediyor': '#f59e0b',
  'Beklemede': '#6b7280', 'Müşteri Yanıtı Bekleniyor': '#ec4899',
  'Çözüldü': '#10b981', 'Kapatıldı': '#ef4444'
};


const MONTHS_TR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

export default function ReportsPage() {
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    Promise.all([api.get('/tickets'), api.get('/customers')])
      .then(([tRes, cRes]) => { setTickets(tRes.data); setCustomers(cRes.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filterByPeriod = (data) => {
    if (period === 'all') return data;
    const now = new Date();
    return data.filter(t => {
      const d = new Date(t.CreatedAt);
      if (period === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      if (period === 'quarter') {
        const q = Math.floor(now.getMonth() / 3);
        const tq = Math.floor(d.getMonth() / 3);
        return tq === q && d.getFullYear() === now.getFullYear();
      }
      if (period === 'year') return d.getFullYear() === now.getFullYear();
      return true;
    });
  };

  const filtered = filterByPeriod(tickets);

  // Stats
  const totalTickets = filtered.length;
  const resolvedTickets = filtered.filter(t => t.Status === 'Çözüldü' || t.Status === 'Kapatıldı').length;
  const successRate = totalTickets > 0 ? Math.round((resolvedTickets / totalTickets) * 100) : 0;
  const avgResolutionDays = (() => {
    const resolved = filtered.filter(t => t.ClosedAt || t.Status === 'Çözüldü');
    if (resolved.length === 0) return 0;
    const total = resolved.reduce((sum, t) => {
      const end = t.ClosedAt ? new Date(t.ClosedAt) : new Date(t.UpdatedAt);
      return sum + (end - new Date(t.CreatedAt)) / (1000 * 60 * 60 * 24);
    }, 0);
    return Math.round(total / resolved.length * 10) / 10;
  })();

  // Status dağılımı
  const statusGroups = {};
  filtered.forEach(t => { statusGroups[t.Status] = (statusGroups[t.Status] || 0) + 1; });

  // Category dağılımı
  const categoryGroups = {};
  filtered.forEach(t => { categoryGroups[t.Category] = (categoryGroups[t.Category] || 0) + 1; });

  // Müşteri bazlı
  const customerGroups = {};
  filtered.forEach(t => {
    const name = t.CustomerName || t.ContactName || 'Bilinmiyor';
    if (!customerGroups[name]) customerGroups[name] = { total: 0, resolved: 0 };
    customerGroups[name].total++;
    if (t.Status === 'Çözüldü' || t.Status === 'Kapatıldı') customerGroups[name].resolved++;
  });

  // Aylık trend
  const monthlyData = {};
  filtered.forEach(t => {
    const d = new Date(t.CreatedAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!monthlyData[key]) monthlyData[key] = { month: MONTHS_TR[d.getMonth()], year: d.getFullYear(), total: 0, resolved: 0 };
    monthlyData[key].total++;
    if (t.Status === 'Çözüldü' || t.Status === 'Kapatıldı') monthlyData[key].resolved++;
  });
  const monthlyArr = Object.values(monthlyData).slice(-12);
  const maxMonthly = Math.max(...monthlyArr.map(m => m.total), 1);

  // Personel bazlı
  const staffGroups = {};
  filtered.forEach(t => {
    if (t.AssignedToName) {
      if (!staffGroups[t.AssignedToName]) staffGroups[t.AssignedToName] = { total: 0, resolved: 0 };
      staffGroups[t.AssignedToName].total++;
      if (t.Status === 'Çözüldü' || t.Status === 'Kapatıldı') staffGroups[t.AssignedToName].resolved++;
    }
  });

  // Priority dağılımı
  const priorityGroups = {};
  filtered.forEach(t => { priorityGroups[t.Priority] = (priorityGroups[t.Priority] || 0) + 1; });

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Raporlar</h1>
        <div className="flex items-center gap-2">
          {[
            { key: 'all', label: 'Tümü' }, { key: 'month', label: 'Bu Ay' },
            { key: 'quarter', label: 'Bu Çeyrek' }, { key: 'year', label: 'Bu Yıl' }
          ].map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${period === p.key ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-1">
        {[
          { key: 'overview', label: 'Genel Bakış', icon: BarChart3 },
          { key: 'customers', label: 'Müşteri Analizi', icon: Users },
          { key: 'trends', label: 'Trendler', icon: TrendingUp },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all ${
              activeTab === tab.key ? 'bg-white text-blue-700 border border-b-0 border-slate-200 -mb-[1px]' : 'text-slate-500 hover:text-slate-700'
            }`}>
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Toplam Talep', value: totalTickets, color: 'from-blue-500 to-blue-600', icon: BarChart3 },
              { label: 'Çözülen', value: resolvedTickets, color: 'from-emerald-500 to-green-600', icon: TrendingUp },
              { label: 'Başarı Oranı', value: `%${successRate}`, color: 'from-purple-500 to-purple-600', icon: PieChart },
              { label: 'Ort. Çözüm Süresi', value: `${avgResolutionDays} gün`, color: 'from-amber-500 to-orange-600', icon: Calendar },
            ].map((kpi, i) => (
              <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                <div className={`w-10 h-10 bg-gradient-to-br ${kpi.color} rounded-xl flex items-center justify-center mb-3`}>
                  <kpi.icon size={20} className="text-white" />
                </div>
                <p className="text-2xl font-bold text-slate-800">{kpi.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{kpi.label}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Distribution */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
              <h3 className="text-base font-semibold text-slate-800 mb-4">Statü Dağılımı</h3>
              <div className="space-y-3">
                {Object.entries(statusGroups).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600">{status}</span>
                      <span className="text-xs font-bold text-slate-800">{count} ({totalTickets > 0 ? Math.round(count / totalTickets * 100) : 0}%)</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3">
                      <div className="h-3 rounded-full transition-all duration-500" style={{ width: `${(count / totalTickets) * 100}%`, backgroundColor: statusColors[status] || '#6b7280' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Priority */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
              <div className="border-t border-slate-100 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Öncelik Dağılımı</h4>
                <div className="flex gap-3">
                  {Object.entries(priorityGroups).map(([pri, cnt]) => (
                    <div key={pri} className={`flex-1 text-center p-3 rounded-xl ${
                      pri === 'Acil' ? 'bg-red-50' : pri === 'Yüksek' ? 'bg-orange-50' : pri === 'Normal' ? 'bg-blue-50' : 'bg-slate-50'
                    }`}>
                      <p className="text-lg font-bold text-slate-800">{cnt}</p>
                      <p className="text-xs text-slate-500">{pri}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Category Distribution */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
              <h3 className="text-base font-semibold text-slate-800 mb-4">Kategori Dağılımı</h3>
              <div className="space-y-2">
                {Object.entries(categoryGroups).sort((a, b) => b[1] - a[1]).map(([cat, count], i) => (
                  <div key={cat} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center text-white text-xs font-bold">{i + 1}</div>
                    <div className="flex-1"><p className="text-sm font-medium text-slate-700">{cat}</p></div>
                    <span className="text-sm font-bold text-slate-800">{count}</span>
                    <div className="w-20 bg-slate-100 rounded-full h-2"><div className="h-2 rounded-full bg-blue-500" style={{ width: `${(count / totalTickets) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Staff Performance */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
              <h3 className="text-base font-semibold text-slate-800 mb-4">Personel Performansı</h3>
              <div className="space-y-3">
                {Object.entries(staffGroups).sort((a, b) => b[1].total - a[1].total).map(([name, data]) => {
                  const pct = data.total > 0 ? Math.round(data.resolved / data.total * 100) : 0;
                  return (
                    <div key={name} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">{name.charAt(0)}</div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">{name}</p>
                        <p className="text-xs text-slate-400">{data.total} talep • {data.resolved} çözülen</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-bold ${pct >= 80 ? 'bg-emerald-100 text-emerald-700' : pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        %{pct}
                      </div>
                    </div>
                  );
                })}
                {Object.keys(staffGroups).length === 0 && <p className="text-center text-slate-400 py-4 text-sm">Henüz atanmış talep yok</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Analysis */}
      {activeTab === 'customers' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-base font-semibold text-slate-800">Müşteri Bazlı Analiz</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Müşteri</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Toplam</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Çözülen</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Açık</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Başarı</th>
                    <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Yoğunluk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Object.entries(customerGroups).sort((a, b) => b[1].total - a[1].total).map(([name, data]) => {
                    const openCount = data.total - data.resolved;
                    const pct = data.total > 0 ? Math.round(data.resolved / data.total * 100) : 0;
                    const intensity = data.total;
                    return (
                      <tr key={name} className="hover:bg-slate-50">
                        <td className="px-5 py-3 font-medium text-sm text-slate-800">{name}</td>
                        <td className="px-5 py-3 text-center text-sm font-bold text-slate-800">{data.total}</td>
                        <td className="px-5 py-3 text-center text-sm text-emerald-600 font-medium">{data.resolved}</td>
                        <td className="px-5 py-3 text-center text-sm text-amber-600 font-medium">{openCount}</td>
                        <td className="px-5 py-3 text-center">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${pct >= 80 ? 'bg-emerald-100 text-emerald-700' : pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>%{pct}</span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-100 rounded-full h-2 max-w-[100px]">
                              <div className={`h-2 rounded-full ${intensity > 10 ? 'bg-red-500' : intensity > 5 ? 'bg-amber-500' : 'bg-blue-500'}`}
                                style={{ width: `${Math.min((intensity / Math.max(...Object.values(customerGroups).map(v => v.total))) * 100, 100)}%` }} />
                            </div>
                            <span className="text-xs text-slate-500">{intensity > 10 ? 'Yüksek' : intensity > 5 ? 'Orta' : 'Düşük'}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Trends */}
      {activeTab === 'trends' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
            <h3 className="text-base font-semibold text-slate-800 mb-6">Aylık Talep Trendi</h3>
            <div className="flex items-end gap-2 h-64">
              {monthlyArr.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                  <div className="w-full flex flex-col items-center gap-1 flex-1 justify-end">
                    <span className="text-xs font-bold text-slate-700">{m.total}</span>
                    <div className="w-full max-w-[40px] bg-blue-500 rounded-t-lg transition-all duration-500 hover:bg-blue-600"
                      style={{ height: `${(m.total / maxMonthly) * 100}%`, minHeight: m.total > 0 ? '8px' : '2px' }} />
                    <div className="w-full max-w-[40px] bg-emerald-500 rounded-t-lg transition-all duration-500 hover:bg-emerald-600 -mt-1"
                      style={{ height: `${(m.resolved / maxMonthly) * 100}%`, minHeight: m.resolved > 0 ? '4px' : '0' }} />
                  </div>
                  <p className="text-[10px] text-slate-500 mt-2 font-medium">{m.month}</p>
                </div>
              ))}
              {monthlyArr.length === 0 && <p className="text-center text-slate-400 w-full py-20">Henüz veri yok</p>}
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-blue-500" /><span className="text-xs text-slate-500">Toplam</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-emerald-500" /><span className="text-xs text-slate-500">Çözülen</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
